export type FileCategory = "image" | "video" | "audio" | "document" | "other";

/**
 * Turns an object key into a human-friendly filename. Keys are minted as
 * `uploads/<timestamp>_<name>` (or `uploads/<ownerId>/<timestamp>_<name>` for external
 * users), so we take the last path segment and drop the leading `<timestamp>_`.
 * A provided `filename` (set on freshly uploaded files) always wins.
 */
export const getDisplayFileName = (key: string, filename?: string): string => {
  if (filename) return filename;
  const last = key.split("/").pop() || key;
  return last.replace(/^\d+_/, "");
};

/**
 * Formats a timestamp as a short relative string ("2 hours ago"). Returns "" for
 * invalid input. Pair with the absolute date in a tooltip.
 */
export const formatRelativeTime = (dateString: string): string => {
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return "just now";
  const units: [number, string][] = [
    [60, "minute"],
    [60, "hour"],
    [24, "day"],
    [30, "month"],
    [12, "year"],
  ];
  let value = seconds / 60; // minutes
  let unit = "minute";
  for (let i = 1; i < units.length; i++) {
    if (Math.abs(value) < units[i][0]) break;
    value /= units[i][0];
    unit = units[i][1];
  }
  const rounded = Math.round(value);
  return `${rounded} ${unit}${rounded !== 1 ? "s" : ""} ago`;
};

/**
 * Short, human-friendly label for a file's type chip: the uppercased extension
 * ("PDF", "DOCX", "ZIP") when available, otherwise the top-level mime type — so
 * documents/archives no longer all read as the unhelpful "application".
 */
export const getFileTypeLabel = (key: string, type: string, filename?: string): string => {
  const name = getDisplayFileName(key, filename);
  const ext = name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
  if (ext && ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) return ext.toUpperCase();
  const top = type.split("/")[0];
  return top ? top.charAt(0).toUpperCase() + top.slice(1) : "File";
};

/**
 * Coarse category used for filtering and choosing a preview icon.
 */
export const getFileCategory = (type: string): FileCategory => {
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  if (
    type === "application/pdf" ||
    type.startsWith("text/") ||
    type.includes("word") ||
    type.includes("officedocument")
  ) {
    return "document";
  }
  return "other";
};
